<!-- ═══════════════════════════════════════════════════════════
     TMS Header – beautiful, responsive, accessible
     Controls: Language | Font Size | Day/Night | User Avatar
     ═══════════════════════════════════════════════════════════ -->
<!-- Header collapsed by hand (or Focus mode): a thin strip in the page flow brings it back. Phone only. -->
<button type="button" class="tms-hdr-restore" *ngIf="showHeader && menuStyle.headerHidden()" (click)="menuStyle.restoreHeader()"
  [attr.aria-label]="'menuDock.showHeader' | t">
  <i class="pi pi-chevron-down" aria-hidden="true"></i>
  <span>{{ 'menuDock.showHeader' | t }}</span>
</button>
<header class="tms-header" [class.tms-hdr-glass]="glassHeader" [class.tms-hdr-slim]="slim" *ngIf="showHeader && !menuStyle.headerHidden()">
  <!-- ── Left: Hamburger + Logo ──────────────────────────────── -->
  <div class="tms-hdr-left">
    <button
      pButton
      type="button"
      id="tms-tour-menu-btn"
      icon="pi pi-bars"
      class="p-button-text p-button-rounded tms-hdr-icon-btn"
      *ngIf="showMenuButton && menuStyle.headerMenuButton()"
      [pTooltip]="'Menu' | t"
      tooltipPosition="bottom"
      appendTo="body"
      (click)="showHideMenu()"
    ></button>

    <a
      id="tms-tour-logo"
      href="https://www.lumberlinq.com"
      class="tms-hdr-logo-wrap"
      target="_blank"
      rel="noopener noreferrer"
    >
      <!-- 2026-09-28: if the logo cannot load (empty/old file, expired link, branding removed) fall back instead of a broken image. -->
      <img [src]="companyLogoUrl" [alt]="'LumberLinq' | t" class="tms-hdr-logo" (error)="onCompanyLogoError()" />
    </a>
    <span
      *ngIf="betaService.betaMode$ | async"
      class="tms-beta-pill"
      title="This app is in beta"
    >BETA</span>
  </div>

  <!-- ── Centre: search pill (new glass look, 768px and wider). Opens the same search dialog as the icon below;
       the "/" key still works. The classic look does not show this zone. ───────────── -->
  <div class="tms-hdr-center" *ngIf="glassHeader && showUserProfileButton && canSearch">
    <button type="button" class="tms-hdr-searchpill" (click)="openSearchFromPill()" [attr.aria-label]="'unifiedSearch.trigger' | t">
      <i class="pi pi-search"></i>
      <span class="tms-hdr-searchtext">{{ 'header.searchHint' | t }}</span>
      <kbd>/</kbd>
    </button>
  </div>

  <!-- ── Right: Controls + User ──────────────────────────────── -->
  <div class="tms-hdr-right">

    <!-- ══ Mobile: single preferences icon (hidden ≥768px) ═════ -->
    <button
      pButton
      type="button"
      icon="pi pi-sliders-h"
      class="p-button-text tms-hdr-icon-btn tms-pref-mobile-btn"
      [pTooltip]="'header.preferences' | t"
      tooltipPosition="bottom"
      appendTo="body"
      (click)="prefPanelVisible = true"
    ></button>

    <!-- ══ Preferences Group: Language · Font · Theme ══════════ -->
    <div class="tms-ctrl-cluster" id="tms-tour-language">

      <!-- Language -->
      <span class="tms-cluster-item"
        [pTooltip]="'common.language' | t"
        tooltipPosition="bottom"
        appendTo="body"
      >
        <p-select
          [options]="languages"
          [(ngModel)]="selectedLanguage"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          styleClass="tms-cluster-drop tms-lang-ctrl"
          (onChange)="onLanguageChange($event)"
        >
          <ng-template pTemplate="selectedItem" let-lang>
            <span class="tms-cluster-val tms-lang-val">
              <i class="pi pi-globe tms-cluster-icon"></i>
              <span class="tms-lang-code">{{ lang?.code }}</span>
            </span>
          </ng-template>
          <ng-template pTemplate="item" let-item>
            <div class="tms-ctrl-inner">
              <span class="tms-lang-flag">{{ item.flag }}</span>
              <span class="tms-ctrl-text">{{ item.label }}</span>
            </div>
          </ng-template>
        </p-select>
      </span>

      <!-- Font Size -->
      <span class="tms-cluster-item" *ngIf="showFontSize"
        id="tms-tour-fontsize"
        [pTooltip]="'header.fontSize' | t"
        tooltipPosition="bottom"
        appendTo="body"
      >
        <p-select
          [options]="fontSizes"
          [(ngModel)]="selectedFontSize"
          optionLabel="label"
          appendTo="body"
          styleClass="tms-cluster-drop tms-font-ctrl"
          (onChange)="onFontSizeChange($event)"
        >
          <ng-template pTemplate="selectedItem" let-item>
            <span class="tms-cluster-val">
              <span class="tms-font-letter" [style.font-size]="(item?.value * 0.8) + 'rem'">A</span>
            </span>
          </ng-template>
          <ng-template pTemplate="item" let-item>
            <div class="tms-ctrl-inner">
              <span class="tms-font-icon" [style.font-size]="(item.value * 0.8) + 'rem'">A</span>
            </div>
          </ng-template>
        </p-select>
      </span>

      <!-- Theme Picker -->
      <span class="tms-cluster-item" *ngIf="showDayNightScheme"
        id="tms-tour-theme"
        [pTooltip]="'header.theme' | t"
        tooltipPosition="bottom"
        appendTo="body"
      >
        <p-select
          [options]="availableThemes"
          [(ngModel)]="selectedTheme"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          styleClass="tms-cluster-drop tms-theme-ctrl"
          (onChange)="onThemeChange($event)"
        >
          <ng-template pTemplate="selectedItem">
            <span class="tms-cluster-val">
              <span class="tms-swatch-wrap">
                <span class="tms-theme-swatch" [style.background]="selectedThemeColor"></span>
              </span>
            </span>
          </ng-template>
          <ng-template pTemplate="item" let-item>
            <div class="tms-ctrl-inner">
              <span class="tms-theme-swatch" [style.background]="getThemeColor(item.value)"></span>
              <span class="tms-ctrl-text">{{ item.label }}</span>
            </div>
          </ng-template>
        </p-select>
      </span>

    </div>
    <!-- ══ End Preferences Group ═══════════════════════════════ -->

    <!-- Separator: preferences | actions -->
    <span class="tms-hdr-sep"></span>

    <!-- Day / Night quick toggle — DESKTOP ONLY. On mobile this exact control already exists
         inside the Preferences drawer below (same toggleTheme() call), so rendering it here too
         spent a permanent toolbar slot on a duplicate. The right-hand cluster overflows the
         viewport on a phone (see .tms-hdr-right in the CSS), and every icon removed here is one
         that stops pushing the notification bell off-screen entirely. -->
    <button
      pButton
      type="button"
      id="tms-tour-darkmode"
      *ngIf="showDayNightScheme && !isThisMobile"
      [icon]="isDarkThemeEnabled ? 'pi pi-sun' : 'pi pi-moon'"
      class="p-button-text tms-hdr-icon-btn tms-theme-btn"
      [pTooltip]="isDarkThemeEnabled ? ('header.lightMode' | t) : ('header.darkMode' | t)"
      tooltipPosition="bottom"
      appendTo="body"
      (click)="toggleTheme()"
    ></button>

    <!-- Product Tour help button — DESKTOP ONLY. "Product Tour" is already one of the items in
         the sidebar drawer's account menu, which is the established account entry point on
         mobile (see side-bar.component.html's identity card), so the tour stays one tap away
         there without costing a toolbar slot on the width where slots actually run out. -->
    <button
      pButton
      type="button"
      id="tms-tour-help"
      *ngIf="!isThisMobile && showTourButton"
      icon="pi pi-question-circle"
      class="p-button-text tms-hdr-icon-btn tms-tour-btn"
      [class.tms-tour-pulse]="!hasSeenTour"
      [pTooltip]="'layout.productTour' | t"
      tooltipPosition="bottom"
      appendTo="body"
      (click)="onTourClick()"
    ></button>

    <!-- Separator: actions | user -->
    <span
      class="tms-hdr-sep tms-sep-user"
      *ngIf="!isThisMobile && showUserProfileButton"
    ></span>

    <!-- Unified Search — one dialog, four modes (Auto/Records/Stock/Calculator), replacing what
         used to be two separate icons (Global Search + Quick Search). Both engines' full internal
         logic still lives in GlobalSearchComponent/QuickSearchComponent, embedded here — this just
         merges their entry point and chrome. See unified-search.component.ts's own doc comment. -->
    <app-unified-search #usearch [anchored]="glassHeader && !isThisMobile" *ngIf="showUserProfileButton"></app-unified-search>

    <!-- Keyboard Shortcuts cheat-sheet — "?" anywhere opens it (Gmail/Slack/Notion/Linear
         convention). No visible trigger icon: it's reached via the shortcut itself or the link
         inside Global Search's help panel. This component renders no button of its own — it's
         the hotkey listener + dialog only.

         A mobile-only "?" button used to sit here (added 2026-08-26) on the reasoning that the
         "?" key is unreachable without a keyboard. Removed: isMobile() is width < 768px, i.e.
         phones, and a device with no physical keyboard has no use for a keyboard-shortcut cheat
         sheet — it was a permanent slot spent on a feature that can't apply at that width, while
         the same overflow was pushing the notification bell off-screen. iPad (>= 768px) is
         unaffected and still uses the desktop "?" hotkey path. -->
    <app-keyboard-shortcuts *ngIf="showUserProfileButton"></app-keyboard-shortcuts>

    <!-- Offline sync status — phone app only (OFFLINE_PLAN.md 1.9 + Phase 2, 2026-09-29). One button:
         a quiet cloud when all is saved, spinning while syncing, "☁ N" while changes wait, amber "⚠ N"
         when the server refused some. Tap → panel: last synced, counts, Sync Issues, Sync now. -->
    <ng-container *ngIf="isNativeApp && showUserProfileButton">
      <button type="button"
        class="tms-sync-chip"
        [class.tms-sync-chip--warn]="outbox.refusedCount() > 0"
        [class.tms-sync-chip--quiet]="!outbox.unsyncedCount() && !syncBusy()"
        [attr.aria-label]="'offline.syncTitle' | t"
        (click)="syncOp.toggle($event)"
      >
        <i class="pi" [ngClass]="syncIcon()"></i>
        <ng-container *ngIf="outbox.refusedCount() > 0; else waitingTpl">
          <span class="tms-sync-chip-num">{{ outbox.refusedCount() }}</span>
          <span class="tms-sync-chip-text">{{ 'offline.notSynced' | t }}</span>
        </ng-container>
        <ng-template #waitingTpl>
          <ng-container *ngIf="outbox.waitingCount() > 0">
            <span class="tms-sync-chip-num">{{ outbox.waitingCount() }}</span>
            <span class="tms-sync-chip-text">{{ 'offline.waitingSync' | t }}</span>
          </ng-container>
        </ng-template>
      </button>

      <p-popover #syncOp appendTo="body" styleClass="tms-sync-pop">
        <div class="tms-sync-pop-body">
          <div class="tms-sync-pop-head">
            <span class="tms-sync-pop-badge"><i class="pi" [ngClass]="syncIcon()"></i></span>
            <div>
              <div class="tms-sync-pop-title">{{ 'offline.syncTitle' | t }}</div>
              <div class="tms-sync-pop-sub">{{ lastSyncedText() }}</div>
            </div>
          </div>

          <div class="tms-sync-pop-row">
            <i class="pi pi-cloud-upload"></i>
            <span>{{ 'offline.waitingSync' | t }}</span>
            <b>{{ outbox.waitingCount() }}</b>
          </div>
          <a class="tms-sync-pop-row tms-sync-pop-row--link" routerLink="/sync-issues" (click)="syncOp.hide()">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ 'offline.notSynced' | t }}</span>
            <b>{{ outbox.refusedCount() }}</b>
            <i class="pi pi-angle-right tms-sync-pop-chev"></i>
          </a>

          <p class="tms-sync-pop-offline" *ngIf="!networkStatusService.isOnline">
            <i class="pi pi-wifi"></i>
            <span>{{ 'offline.youAreOffline' | t }}</span>
          </p>

          <button pButton type="button" class="w-full tms-sync-pop-btn" icon="pi pi-sync"
            [label]="'offline.syncNow' | t"
            [loading]="syncBusy()"
            [disabled]="!networkStatusService.isOnline || syncBusy()"
            (click)="syncNow()"></button>
        </div>
      </p-popover>
    </ng-container>

    <!-- "+ New" (glass look, desktop): the same create shortcuts the sidebar offers, only those this user may use. -->
    <button type="button" class="tms-hdr-newbtn" *ngIf="glassHeader && !isThisMobile && showUserProfileButton && canCreate"
      [attr.aria-label]="'header.newBtn' | t" (click)="openNewMenu($event)">
      <i class="pi pi-plus"></i><span class="tms-hdr-newtext">{{ 'header.newBtn' | t }}</span>
    </button>
    <p-menu #newMenu [model]="newItems" [popup]="true" appendTo="body"></p-menu>

    <!-- Notification bell (docs/notification-build/PLAN.md, Phase 1) -->
    <app-notification-bell *ngIf="showUserProfileButton"></app-notification-bell>

    <!-- User avatar + context menu -->
    <div id="tms-tour-avatar" *ngIf="!isThisMobile && showUserProfileButton" class="tms-hdr-user">
      <ng-container *ngIf="getAvatarImgSrc() as avatarSrc; else avatarInitials">
        <p-avatar
          #avatar1
          [image]="avatarSrc"
          (onImageError)="onAvatarImageError()"
          styleClass="tms-hdr-avatar"
          size="normal"
          shape="circle"
          [ngStyle]="{ cursor: 'pointer' }"
          (click)="isAvatarClickable && toggleMenu($event)"
        ></p-avatar>
      </ng-container>
      <ng-template #avatarInitials>
        <p-avatar
          #avatar1
          [label]="userProfile?.firstName?.charAt(0) || 'U'"
          styleClass="tms-hdr-avatar"
          size="normal"
          [style]="{
            background: 'linear-gradient(135deg,#1684ac,#1fa39a)',
            color: '#fff',
          }"
          shape="circle"
          (click)="toggleMenu($event)"
        ></p-avatar>
      </ng-template>
      <p-menu [model]="items" #menu [popup]="true" appendTo="body"></p-menu>
    </div>

  </div>

  <!-- Small tab on the header's lower edge (phone only): collapses the header to give the page the height. -->
  <button type="button" class="tms-hdr-collapse" *ngIf="menuStyle.isPhone()" (click)="menuStyle.setHeaderCollapsed(true)"
    [attr.aria-label]="'menuDock.hideHeader' | t" [attr.title]="'menuDock.hideHeader' | t">
    <i class="pi pi-chevron-up" aria-hidden="true"></i>
  </button>
</header>

<!-- ══ Mobile Preferences Sidebar ══════════════════════════════ -->
<p-drawer
  [(visible)]="prefPanelVisible"
  position="bottom"
  appendTo="body"
  [style]="{ height: 'auto' }"
  styleClass="tms-pref-sidebar"
  [transitionOptions]="'280ms cubic-bezier(0.32, 0.72, 0, 1)'"
  (onHide)="onPrefPanelHide()"
>
  <!-- Bottom SHEET, not a right-hand drawer. This panel is mobile-only (its trigger button is
       hidden at >=768px, where the header shows the controls inline instead), and on a phone a
       full-height right drawer was the wrong shape twice over: it put the close button in the
       hardest corner to reach one-handed, and it reserved the whole screen height for four rows,
       leaving roughly 60% of the sheet empty. Sized to its content and anchored to the bottom,
       it lands where the thumb already is — the same idiom iOS uses for its own sheets. The
       easing is the iOS sheet curve; the previous '0ms' meant it simply snapped into place. -->
  <ng-template pTemplate="header">
    <span class="tms-pref-sidebar-title">
      <i class="pi pi-sliders-h"></i>
      {{ 'header.preferences' | t }}
    </span>
  </ng-template>

  <div class="tms-pref-sidebar-body">

    <!-- Language -->
    <div class="tms-pref-row">
      <label class="tms-pref-label">
        <i class="pi pi-globe"></i>
        {{ 'common.language' | t }}
      </label>
      <p-select
        [options]="languages"
        [(ngModel)]="selectedLanguage"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        styleClass="tms-pref-drop"
        (onChange)="onLanguageChange($event)"
      >
        <ng-template pTemplate="selectedItem" let-lang>
          <span>{{ lang?.flag }} {{ lang?.label }}</span>
        </ng-template>
        <ng-template pTemplate="item" let-item>
          <span>{{ item.flag }} {{ item.label }}</span>
        </ng-template>
      </p-select>
    </div>

    <!-- Font Size -->
    <div class="tms-pref-row" *ngIf="showFontSize">
      <!-- pi-text is NOT a real PrimeIcon (verified against primeicons.css) — it silently
           rendered nothing, which is why this was the only row in the sheet without an icon. -->
      <label class="tms-pref-label">
        <i class="pi pi-align-left"></i>
        {{ 'header.fontSize' | t }}
      </label>
      <p-select
        [options]="fontSizes"
        [(ngModel)]="selectedFontSize"
        optionLabel="label"
        appendTo="body"
        styleClass="tms-pref-drop"
        (onChange)="onFontSizeChange($event)"
      >
        <ng-template pTemplate="selectedItem" let-item>
          <span [style.font-size]="(item?.value * 0.8) + 'rem'" style="font-weight:700">A</span>
          <span style="margin-left:6px">{{ item?.label }}</span>
        </ng-template>
        <ng-template pTemplate="item" let-item>
          <span [style.font-size]="(item.value * 0.8) + 'rem'" style="font-weight:700">A</span>
          <span style="margin-left:6px">{{ item.label }}</span>
        </ng-template>
      </p-select>
    </div>

    <!-- Theme -->
    <div class="tms-pref-row" *ngIf="showDayNightScheme">
      <label class="tms-pref-label">
        <i class="pi pi-palette"></i>
        {{ 'header.theme' | t }}
      </label>
      <p-select
        [options]="availableThemes"
        [(ngModel)]="selectedTheme"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        styleClass="tms-pref-drop"
        (onChange)="onThemeChange($event)"
      >
        <ng-template pTemplate="selectedItem" let-item>
          <span class="tms-theme-swatch" [style.background]="selectedThemeColor" style="display:inline-block;width:0.7rem;height:0.7rem;border-radius:50%;margin-right:6px"></span>
          <span>{{ item?.label }}</span>
        </ng-template>
        <ng-template pTemplate="item" let-item>
          <span class="tms-theme-swatch" [style.background]="getThemeColor(item.value)" style="display:inline-block;width:0.7rem;height:0.7rem;border-radius:50%;margin-right:6px"></span>
          <span>{{ item.label }}</span>
        </ng-template>
      </p-select>
    </div>

    <!-- Dark Mode.
         Was a bare icon button, which read as decoration rather than a control and gave no
         at-a-glance on/off state — the three rows above it all have a real control on the right,
         so this one broke the rhythm. A switch states the current value without being read.
         The label is also now a stable noun ("Dark Mode") instead of flipping between
         "Light Mode"/"Dark Mode": paired with a switch, a label that changes with the state makes
         it genuinely ambiguous whether it describes the current mode or the one you'd get. -->
    <div class="tms-pref-row" *ngIf="showDayNightScheme">
      <!-- Fixed moon, not a state-dependent sun/moon. The row's label is the stable noun
           "Dark Mode" and the switch already states the value — an icon that flips too left the
           row saying "Dark Mode" beside a sun, which reads as a contradiction. -->
      <label class="tms-pref-label" for="tmsPrefDarkMode">
        <i class="pi pi-moon"></i>
        {{ 'header.darkMode' | t }}
      </label>
      <p-toggleswitch
        inputId="tmsPrefDarkMode"
        [ngModel]="isDarkThemeEnabled"
        (ngModelChange)="toggleTheme()"
      ></p-toggleswitch>
    </div>

    <!-- Menu style (2026-10-07): the dock menu (bottom dock on a phone, side rail on a tablet) or the classic side drawer. -->
    <div class="tms-pref-row">
      <label class="tms-pref-label" for="tmsPrefDockMenu">
        <i class="pi pi-window-maximize"></i>
        {{ 'menuDock.useDockRow' | t }}
      </label>
      <p-toggleswitch
        inputId="tmsPrefDockMenu"
        [ngModel]="menuStyle.dockOn()"
        (ngModelChange)="menuStyle.toggleStyle()"
      ></p-toggleswitch>
    </div>

  </div>
</p-drawer>

<!-- appendTo="body" on all three overlays in this file (these two plus the Preferences drawer
     above) is REQUIRED, not cosmetic. <header class="header"> in app.component.html is now
     position:sticky with a z-index, which makes it a STACKING CONTEXT — and this component
     renders inside it. An overlay declared here without appendTo is confined to that context, so
     anything on the page with a higher z-index paints over it: the SpeedDial FAB sits at 9000 and
     did exactly that to the Preferences sheet, which then looked dimmed and unresponsive because
     it was underneath. Sending them to <body> takes them out of the header's context entirely.
     Every other overlay in this file (selects, tooltips, the avatar menu) already does this. -->
<p-confirmDialog key="headerLogoutConfirm" appendTo="body" [baseZIndex]="20000" styleClass="ll-confirm-big"></p-confirmDialog>
<p-confirmDialog key="switchCompanyConfirm" appendTo="body" [baseZIndex]="20000" [style]="{ width: '28rem' }" [breakpoints]="{ '640px': '92vw' }"></p-confirmDialog>
